import { useMemo } from 'react';
import { notifications } from '@mantine/notifications';
import { Button, HoverCard, Loader, Text } from '@mantine/core';
import { IconBulb, IconCheck, IconExternalLink, IconX } from '@tabler/icons-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useAtom } from 'jotai';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useAwareZindex } from '~/hooks/context/useZIndex';
import { api } from '~/trpc-extra/api';
import { useAcceptAutohealChanges } from '~/hooks/test/use-accept-autoheal-changes';
import { isAcceptingAutohealChangesAtom } from '~/atoms/atoms';
import type { GetSuiteTestRunOutputType } from '~/server/api/routers/test-suite-run-router';
import type { AutohealSuggestion } from '~/types';

const HOVER_CARD_Z_INDEX_OFFSET = 30;
// Hide the feature for now until it's more polished
const AUTOHEAL_SUGGESTIONS_ENABLED = false;

type AutohealSuggestionQuickViewProps = {
  suggestion?: AutohealSuggestion;
  latestAttempt?: GetSuiteTestRunOutputType['TestRunAttempts'][0];
  suiteRunId?: string;
  testRunId?: string;
  showLearnMoreLink?: boolean;
  testStatus?: string;
  isLatestSuiteRun?: boolean;
};

export default function AutohealSuggestionQuickView(props: AutohealSuggestionQuickViewProps) {
  if (!AUTOHEAL_SUGGESTIONS_ENABLED) {
    return null;
  }

  return <AutohealSuggestionQuickViewEnabled {...props} />;
}

function AutohealSuggestionQuickViewEnabled({
  suggestion,
  latestAttempt,
  suiteRunId,
  testRunId,
  showLearnMoreLink,
  testStatus,
  isLatestSuiteRun,
}: AutohealSuggestionQuickViewProps) {
  const projectId = useProjectId();
  const router = useRouter();
  const trpcUtils = api.useUtils();
  const zIndexBase = useAwareZindex();
  const attemptId = latestAttempt?.id;

  // Use the same atom for both loading state and optimistic update
  const [isAcceptingAutohealChanges, setIsAcceptingAutohealChanges] = useAtom(
    isAcceptingAutohealChangesAtom(latestAttempt?.outputTestContentId || ''),
  );

  const suggestionQuery = api.testRun.getAutohealSuggestion.useQuery(
    { attemptId: attemptId ?? '' },
    {
      enabled: !!attemptId && isLatestSuiteRun && (!suggestion || suggestion.attemptId !== attemptId),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  const effectiveSuggestion = suggestion ?? suggestionQuery.data?.suggestion;

  const { mutateAsync: updateSuggestionDecision, isPending: isDecisionUpdating } =
    api.testRun.updateAutohealSuggestionDecision.useMutation({
      onSuccess: async () => {
        await Promise.all(
          [
            suiteRunId ? trpcUtils.suiteRun.get.invalidate({ runId: suiteRunId }) : trpcUtils.suiteRun.get.invalidate(),
            testRunId && trpcUtils.testRun.get.invalidate({ runId: testRunId }),
            suggestionQuery.refetch(),
          ].filter(Boolean),
        );
      },
      onError: (error) => {
        console.error(error);
        notifications.show({ message: 'Failed to update auto-heal suggestion', color: 'red' });
      },
    });

  const { acceptChanges, isPending: isAcceptChangesPending } = useAcceptAutohealChanges({
    testContentId: latestAttempt?.outputTestContentId || '',
  });

  const aiChanges = useMemo(() => {
    const changes = latestAttempt?.executionOutput?.aiChanges;
    return Array.isArray(changes) ? changes : [];
  }, [latestAttempt?.executionOutput]);

  const hasSuccessfulAutoheal = aiChanges.length > 0 && aiChanges.every((change) => change?.isSuccess);
  const isLoading = suggestionQuery.isLoading || suggestionQuery.isFetching;
  const showSuggestion =
    isLatestSuiteRun &&
    (testStatus === 'PASSED' || testStatus === 'FLAKY') &&
    !isAcceptingAutohealChanges && // Hide immediately when accepting changes
    (isLoading ||
      (!!effectiveSuggestion &&
        effectiveSuggestion.shouldSuggest &&
        effectiveSuggestion.decision === 'pending' &&
        hasSuccessfulAutoheal));

  const handleDecision = async (decision: 'approved' | 'dismissed') => {
    if (!attemptId) {
      notifications.show({ message: 'Test attempt not found', color: 'red' });
      return;
    }

    // Hide the suggestion immediately for both approve and dismiss
    setIsAcceptingAutohealChanges(true);

    try {
      await updateSuggestionDecision({ attemptId, decision });
    } catch (error) {
      // Restore visibility on failure
      setIsAcceptingAutohealChanges(false);
      console.error(error);
      const message =
        decision === 'approved' ? 'Failed to accept auto-heal suggestion' : 'Failed to dismiss auto-heal suggestion';
      notifications.show({ message, color: 'red' });
    }
  };

  if (!showSuggestion) {
    return null;
  }

  const handleQuickAccept = async () => {
    if (!latestAttempt?.outputTestContentId) {
      notifications.show({ message: 'Auto-heal output is not available for this attempt', color: 'red' });
      return;
    }

    // Hide the suggestion immediately when accepting changes
    setIsAcceptingAutohealChanges(true);

    try {
      await acceptChanges();
      await handleDecision('approved');
      // Success - keep hidden (handled by cache invalidation)
    } catch (error) {
      // Failure - restore the suggestion visibility
      setIsAcceptingAutohealChanges(false);
      console.error(error);
      notifications.show({ message: 'Failed to accept auto-heal changes', color: 'red' });
    }
  };

  const handleLearnMore = () => {
    if (!projectId || !testRunId) {
      return;
    }

    const fromQuery = router.query.runOrSuiteId;
    const runOrSuiteParam =
      typeof fromQuery === 'string' ? fromQuery
      : Array.isArray(fromQuery) && fromQuery.length > 0 ? fromQuery[0]
      : suiteRunId ? `g_${suiteRunId}`
      : testRunId;

    void router.push(`/project/${projectId}/history/${runOrSuiteParam}/run/${testRunId}?tab=diffView`);
  };

  // If we're loading, show loading badge without popover functionality
  if (isLoading) {
    return (
      <div className="flex flex-shrink-0 cursor-pointer items-center gap-1 rounded-md bg-yellow-500/20 px-2 py-1 text-xs font-medium dark:text-white">
        <Loader
          size={12}
          color="yellow"
        />
        Suggestion
      </div>
    );
  }

  if (!effectiveSuggestion) {
    return null;
  }

  const { stats } = effectiveSuggestion;
  const consecutiveStreakCount = stats.currentStreakLength;
  const currentStreakPassPercentage = Math.round(stats.currentStreakSuccessRatio * 100);

  return (
    <HoverCard
      width={320}
      shadow="md"
      withArrow
      openDelay={300}
      closeDelay={400}
      zIndex={zIndexBase + HOVER_CARD_Z_INDEX_OFFSET}
    >
      <HoverCard.Target>
        <Link
          href={{
            pathname: router.pathname,
            query: { ...router.query, testRunDrawer: testRunId },
          }}
          as={`/project/${projectId}/history/${router.query.runOrSuiteId}/run/${testRunId}`}
          className="flex w-fit flex-shrink-0 cursor-pointer items-center gap-1 rounded-md bg-yellow-500/20 px-2 py-1 text-xs font-medium transition-colors hover:bg-yellow-500/30 dark:text-white"
        >
          <IconBulb size={12} />
          Suggestion
        </Link>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <div className="flex max-w-sm flex-col gap-3 text-sm">
          <div className="font-medium text-gray-900 dark:text-gray-100">Make future runs cheaper and faster</div>
          <Text
            size="xs"
            className="text-gray-600 dark:text-gray-400"
          >
            This test required autoheal for all recent {consecutiveStreakCount} runs. The test passed{' '}
            {stats.currentStreakSuccessCount}/{consecutiveStreakCount} ({currentStreakPassPercentage}%) of these runs.
            Consider saving autoheal changes to avoid unnecessary latency and costs.
            {!!showLearnMoreLink && (
              <Button
                variant="subtle"
                size="compact-xs"
                color="blue"
                onClick={handleLearnMore}
                rightSection={<IconExternalLink size={12} />}
                className="ml-1 hover:bg-transparent"
              >
                Learn more
              </Button>
            )}
          </Text>
          <div className="flex items-center gap-2">
            <Button
              size="xs"
              color="blue"
              variant="light"
              leftSection={<IconCheck size={14} />}
              loading={isAcceptChangesPending}
              disabled={isDecisionUpdating || isAcceptChangesPending}
              onClick={() => void handleQuickAccept()}
            >
              Accept changes
            </Button>
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              leftSection={<IconX size={14} />}
              loading={isDecisionUpdating}
              disabled={isAcceptChangesPending || isDecisionUpdating}
              onClick={() => void handleDecision('dismissed')}
            >
              Dismiss
            </Button>
          </div>
        </div>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}
